import { Alert, Button, Checkbox, Form, Input, Spin, Table, Tag, message } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { ConsentDetails } from "../../../../../helper/consentDetails";
import { serverUrl } from "../../../../../nestserver";

const { TextArea } = Input;

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_COLOURS = {
  approved: "green",
  pending: "orange",
  rejected: "red",
};

const studentName = (record) =>
  `${record?.student?.first_name || ""} ${
    record?.student?.last_name || ""
  }`.trim();

const requiredKeys = (trip) =>
  (trip?.declarations || [])
    .filter((d) => d.required !== false)
    .map((d) => d.key);

/**
 * Parent's consent inbox, listed as a table. Opening a request swaps the table
 * for a full-page consent form — the declarations are ticked one by one, the
 * way an app's first-run permission screen reads, rather than squeezed into a
 * dialog.
 *
 * The declarations come from the form itself: whoever created it wrote them.
 */
const TripConsentList = () => {
  const queryClient = useQueryClient();
  const [activeForm, setActiveForm] = useState(null);
  const [agreed, setAgreed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [form] = Form.useForm();

  const consents = useQuery("/trip/my-consents", async () => {
    const res = await axios.get(`${serverUrl}/trip/my-consents`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const rows = consents?.data?.result?.data || [];

  const canRespond = (record) => {
    const trip = record.trip || {};
    return (
      record.status === "pending" &&
      trip.status === "sent" &&
      moment(trip.consent_deadline).endOf("day").isSameOrAfter(moment())
    );
  };

  const openForm = (record) => {
    setActiveForm(record);
    setAgreed([]);
    setDeclining(false);
    form.resetFields();
  };

  const closeForm = () => {
    setActiveForm(null);
    setAgreed([]);
    setDeclining(false);
  };

  const allRequiredTicked = activeForm
    ? requiredKeys(activeForm.trip).every((key) => agreed.includes(key))
    : false;

  const submit = async (status) => {
    let values;
    try {
      values = await form.validateFields(
        status === "approved" ? undefined : ["remarks"]
      );
    } catch (err) {
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(
        `${serverUrl}/trip/consent/${activeForm._id}`,
        {
          status,
          agreed_declarations: status === "approved" ? agreed : [],
          remarks: values.remarks,
          emergency_contact: values.emergency_contact,
          medical_notes: values.medical_notes,
          signature_name: values.signature_name,
        },
        { headers: authHeader() }
      );
      message.success(res?.data?.result?.message || "Response recorded");
      closeForm();
      queryClient.invalidateQueries("/trip/my-consents");
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (record) => {
    setDownloading(record._id);
    try {
      const res = await axios({
        url: `${serverUrl}/trip/consent/${record._id}/download`,
        method: "GET",
        responseType: "blob",
        headers: { Accept: "application/pdf", ...authHeader() },
      });
      const safeChild = studentName(record).replace(/[^a-zA-Z0-9]/g, "");
      const safeTitle = (record.trip?.title || "Consent").replace(
        /[^a-zA-Z0-9]/g,
        ""
      );
      saveAs(res.data, `Consent_${safeTitle}_${safeChild || "Student"}.pdf`);
    } catch (err) {
      message.error("Could not download the signed form");
    } finally {
      setDownloading(null);
    }
  };

  /* ---------------------------- The form page --------------------------- */

  if (activeForm) {
    const trip = activeForm.trip || {};
    const declarations = trip.declarations || [];

    return (
      <div className="p-8">
        <div className="bg-white rounded-2xl max-w-3xl mx-auto overflow-hidden">
          <div className="px-8 pt-8 pb-6 border-b">
            <Button type="link" className="px-0" onClick={closeForm}>
              &larr; Back to all forms
            </Button>
            <div className="text-2xl font-semibold pt-2">{trip.title}</div>
            <div className="text-sm text-gray-600 pt-1">
              Consent for <b>{studentName(activeForm)}</b>
              {activeForm.class?.name ? ` · ${activeForm.class.name}` : ""}
            </div>
            <div className="text-sm text-gray-600 pt-3 grid gap-1">
              <div>
                <b>Dates:</b> {moment(trip.from).format("DD MMM YYYY")} to{" "}
                {moment(trip.to).format("DD MMM YYYY")}
              </div>
              <div>
                <b>Respond by:</b>{" "}
                {moment(trip.consent_deadline).format("DD MMM YYYY")}
              </div>
            </div>
            <ConsentDetails
              details={trip.description}
              className="text-sm text-gray-700 pt-4"
            />
          </div>

          <Form form={form} layout="vertical" className="px-8 py-6">
            {!declining && (
              <>
                {declarations.length > 0 && (
                  <>
                    <div className="text-base font-semibold pb-1">
                      Please read and tick each statement
                    </div>
                    <div className="text-sm text-gray-500 pb-4">
                      Statements marked required must all be ticked to give
                      consent.
                    </div>

                    <div className="grid gap-3 pb-6">
                      {declarations.map((declaration) => (
                        <label
                          key={declaration.key}
                          className="flex gap-3 border rounded-xl p-4 cursor-pointer hover:border-blue-400"
                        >
                          <Checkbox
                            checked={agreed.includes(declaration.key)}
                            onChange={(e) =>
                              setAgreed((prev) =>
                                e.target.checked
                                  ? [...prev, declaration.key]
                                  : prev.filter((k) => k !== declaration.key)
                              )
                            }
                          />
                          <span className="text-sm">
                            {declaration.label}
                            {declaration.required === false && (
                              <span className="text-gray-400"> (optional)</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                <Form.Item
                  name="emergency_contact"
                  label="Emergency contact number"
                  rules={[
                    {
                      required: true,
                      message: "Give a number we can reach on the day",
                    },
                  ]}
                >
                  <Input placeholder="Phone number reachable during the activity" />
                </Form.Item>

                <Form.Item
                  name="medical_notes"
                  label="Allergies, medication or medical conditions"
                >
                  <TextArea rows={2} placeholder="Write 'None' if there are none" />
                </Form.Item>

                <Form.Item
                  name="signature_name"
                  label="Type your full name to sign"
                  rules={[
                    { required: true, message: "Type your full name to sign" },
                  ]}
                >
                  <Input placeholder="Full name of parent or guardian" />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="remarks"
              label={declining ? "Reason (optional)" : "Note to the school (optional)"}
            >
              <TextArea rows={2} placeholder="Anything the school should know" />
            </Form.Item>

            {!declining && !allRequiredTicked && declarations.length > 0 && (
              <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Tick every required statement above to give consent."
              />
            )}

            <div className="flex gap-3 pt-2">
              {declining ? (
                <>
                  <Button
                    danger
                    type="primary"
                    loading={saving}
                    onClick={() => submit("rejected")}
                  >
                    Confirm decline
                  </Button>
                  <Button onClick={() => setDeclining(false)}>Back</Button>
                </>
              ) : (
                <>
                  <Button
                    type="primary"
                    loading={saving}
                    disabled={!allRequiredTicked && declarations.length > 0}
                    onClick={() => submit("approved")}
                  >
                    Give consent
                  </Button>
                  <Button danger onClick={() => setDeclining(true)}>
                    Decline
                  </Button>
                </>
              )}
            </div>
          </Form>
        </div>
      </div>
    );
  }

  /* ------------------------------ The table ----------------------------- */

  const columns = [
    {
      title: "Consent form",
      width: 260,
      render: (record) => (
        <div>
          <div className="font-semibold">{record.trip?.title}</div>
          {record.trip?.status !== "sent" && (
            <Tag color="default" className="mt-1">
              {record.trip?.status}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Child",
      width: 170,
      render: (record) => (
        <div>
          <div>{studentName(record)}</div>
          <div className="text-xs text-gray-500">{record.class?.name}</div>
        </div>
      ),
    },
    {
      title: "Dates",
      width: 190,
      render: (record) =>
        `${moment(record.trip?.from).format("DD MMM YYYY")} to ${moment(
          record.trip?.to
        ).format("DD MMM YYYY")}`,
    },
    {
      title: "Respond by",
      width: 130,
      render: (record) =>
        moment(record.trip?.consent_deadline).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      width: 160,
      render: (record) => (
        <div>
          <Tag color={STATUS_COLOURS[record.status] || "default"}>
            {record.status}
          </Tag>
          {record.status !== "pending" && (
            <div className="text-xs text-gray-500 pt-1">
              {moment(record.responded_at).format("DD MMM YYYY")}
              {record.signature_name ? ` · ${record.signature_name}` : ""}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      width: 200,
      fixed: "right",
      render: (record) => (
        <div className="flex gap-2">
          {canRespond(record) ? (
            <Button type="primary" size="small" onClick={() => openForm(record)}>
              Open form
            </Button>
          ) : record.status === "pending" ? (
            <span className="text-sm text-gray-400">Closed</span>
          ) : null}
          {record.status === "approved" && (
            <Button
              size="small"
              loading={downloading === record._id}
              onClick={() => downloadPdf(record)}
            >
              Download PDF
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="text-2xl text-orange-500 font-semibold pb-8">
          Consent Forms
        </div>

        {consents.isLoading ? (
          <div className="py-10 text-center">
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="_id"
            className="rounded-2xl"
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1110 }}
            locale={{ emptyText: "No consent forms yet" }}
            expandable={{
              expandedRowRender: (record) => (
                <ConsentDetails
                  details={record.trip?.description}
                  className="text-sm text-gray-700 px-2 py-1"
                />
              ),
              rowExpandable: (record) => !!record.trip?.description,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TripConsentList;
